import { annotatorClient } from './annotatorClient';
import type { AnnotatorProject, Label } from '../types/annotator';

function normalizeLabel(raw: any): Label {
  return {
    id: raw.id,
    name: raw.name,
    color: raw.color ?? raw.colour ?? '#FF0000',
    class_index: raw.class_index,
    annotation_count: raw.annotation_count ?? 0,
  };
}

function normalizeProject(raw: any): AnnotatorProject {
  return { ...raw, labels: Array.isArray(raw.labels) ? raw.labels.map(normalizeLabel) : [] };
}
export async function listProjects(): Promise<AnnotatorProject[]> {
  const data = await annotatorClient.get<any[]>('/projects/');
  return data.map(normalizeProject);
}

export async function getProject(id: number): Promise<AnnotatorProject> {
  const data = await annotatorClient.get<any>(`/projects/${id}`);
  return normalizeProject(data);
}

export async function createProject(payload: {
  name: string;
  description?: string;
  organisation_id: number;
}): Promise<AnnotatorProject> {
  const data = await annotatorClient.post<any>('/projects/', payload);
  return normalizeProject(data);
}

export async function updateProject(
  id: number,
  payload: Partial<{
    name: string;
    description: string;
    output_folder: string;
    delete_output_folder: boolean;
    ftp_device_id: string;
    inference_confidence: number;
    inference_model_id: string | null;
    inference_source_subdirectory: string;
    inference_use_source_subdirectory: boolean;
  }>,
): Promise<AnnotatorProject> {
  const data = await annotatorClient.patch<any>(`/projects/${id}`, payload);
  return normalizeProject(data);
}

export async function deleteProject(id: number): Promise<void> {
  await annotatorClient.delete(`/projects/${id}`);
}

export async function addLabel(projectId: number, payload: { name: string; color: string }): Promise<Label> {
  const data = await annotatorClient.post<any>(`/projects/${projectId}/labels`, payload);
  return normalizeLabel(data);
}

export async function deleteLabel(projectId: number, labelId: number): Promise<void> {
  await annotatorClient.delete(`/projects/${projectId}/labels/${labelId}`);
}

export async function updateLabelColor(projectId: number, labelId: number, colour: string): Promise<Label> {
  const data = await annotatorClient.patch<any>(`/projects/${projectId}/labels/${labelId}`, { colour });
  return normalizeLabel(data);
}

export interface CopyLabelResult {
  copied_images: number;
  dest_label_id: number;
}

export interface CombineSource {
  project_id: number;
  label_ids: number[];
}

export async function combineProjects(payload: {
  name: string;
  description?: string;
  organisation_id: number;
  sources: CombineSource[];
}): Promise<AnnotatorProject> {
  const data = await annotatorClient.post<any>('/projects/combine', payload);
  return normalizeProject(data);
}

export async function copyLabelToProject(
  srcProjectId: number,
  labelId: number,
  destProjectId: number,
  removeFromSource: boolean,
): Promise<CopyLabelResult> {
  return annotatorClient.post<CopyLabelResult>(
    `/projects/${srcProjectId}/labels/${labelId}/copy-to-project`,
    { dest_project_id: destProjectId, remove_from_source: removeFromSource },
  );
}

export interface RollupLabelsResult {
  copied_images: number;
  dest_label_id: number;
}

export async function rollupLabelsToProject(
  srcProjectId: number,
  labelIds: number[],
  destProjectId: number,
  destLabelName: string,
  removeFromSource: boolean,
): Promise<RollupLabelsResult> {
  return annotatorClient.post<RollupLabelsResult>(
    `/projects/${srcProjectId}/labels/rollup-to-project`,
    { label_ids: labelIds, dest_project_id: destProjectId, dest_label_name: destLabelName, remove_from_source: removeFromSource },
  );
}
